<script setup>
import { ref, reactive, onMounted, nextTick, defineExpose } from "vue";

const props = defineProps({
  article: { type: Object, required: true }
});

const emit = defineEmits(["close"]);
/* =============================================
   STATE
============================================= */
const loading = ref(true);
const error = ref(null);

const groups = ref([]);
const selected = ref([]);     // <-- used by detectValidation()
const collapsed = reactive({});
const editorRoot = ref(null);

let autosaveTimer = null;
const AUTOSAVE_DELAY = 400;

/* Expose reactive value for ModalManager.detectValidation() */
defineExpose({ selected });


function findGroupByTypeId(typeId) {
  return groups.value.find(g =>
    g.types.some(t => t.id === typeId)
  );
}

function flashGroupSaved(groupId) {
  flashSaved(`#group-${groupId}`);
}

/* =============================================
   LOAD DATA
============================================= */
async function load() {
  loading.value = true;
  error.value = null;

  try {
    const apiKey = localStorage.getItem("api_key");

    const res = await fetch(`/api/articles/${props.article.id}/types`, {
      headers: {
        "Content-Type": "application/json",
        ...(apiKey ? { "x-api-key": apiKey } : {})
      }
    });

    const data = await res.json();

    groups.value = data.groups || [];
    selected.value = data.selected || [];

    // Normalize / prevent NULL values
    groups.value.forEach((g, gIdx) => {
      // Open active groups, collapse inactive ones
      collapsed[g.id] = !g.active;

      g.sortorder = g.sortorder ?? gIdx + 1;

      g.types.forEach((t, tIdx) => {
        t.sortorder = t.sortorder ?? tIdx + 1;
      });
    });


  } catch (err) {
    console.error("❌ load types failed:", err);
    error.value = "Failed to load type data.";
  }

  loading.value = false;
}

/* =============================================
   SAVE (editor "OK")
============================================= */
async function save() {
  await saveTypes();
}

async function saveTypes({ silent = false } = {}) {
  try {
    const apiKey = localStorage.getItem("api_key");

    await fetch(`/api/articles/${props.article.id}/types`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(apiKey ? { "x-api-key": apiKey } : {})
      },
      body: JSON.stringify({ typeIds: selected.value })
    });

    if (!silent) {
      emit("close", selected.value.length > 0);
    }

    return true;

  } catch (err) {
    console.error("❌ Failed to save article types:", err);
    error.value = "Failed to save type selection.";
    return false;
  }
}


/* =============================================
   CANCEL (editor cancel button)
============================================= */
function cancel() {
  emit("close", selected.value.length > 0);
}

/* =============================================
   TYPE CHECKBOX HANDLER
============================================= */
function toggleType(typeId) {
  const wasSelected = selected.value.includes(typeId);

  if (wasSelected) {
    selected.value = selected.value.filter(x => x !== typeId);
  } else {
    selected.value.push(typeId);
  }

  // Auto-open group
  const group = findGroupByTypeId(typeId);
  if (group) {
    collapsed[group.id] = false;
  }

  // Debounced autosave
  if (autosaveTimer) {
    clearTimeout(autosaveTimer);
  }

  autosaveTimer = setTimeout(async () => {
    const ok = await saveTypes({ silent: true });

    if (ok && group) {
      flashGroupSaved(group.id);
    }
  }, AUTOSAVE_DELAY);
}


/* =============================================
   SORTABLEJS SUPPORT
============================================= */
let Sortable = null;
let groupSortable = null;
const typeSortables = new Map();

function flashSaved(selector) {
  const el = document.querySelector(selector);
  if (!el) return;
  el.classList.add("nz-saved");
  setTimeout(() => el.classList.remove("nz-saved"), 2000);
}

function initGroupSort() {
  if (!Sortable) return;

  const container = document.querySelector(".group-list");
  if (!container) return;

  if (groupSortable) groupSortable.destroy();

  groupSortable = Sortable.create(container, {
    animation: 150,
    handle: ".drag-handle-group",
    ghostClass: "drag-ghost",
    onEnd(evt) {
      updateGroupSortorder();
      flashSaved(`#${evt.item.id} .type-group-header`);
    }
  });
}

function initTypeSort(groupId) {
  if (!Sortable) return;

  const el = document.querySelector(`#group-${groupId} .types-container`);
  if (!el) return;

  if (typeSortables.has(groupId)) {
    typeSortables.get(groupId).destroy();
  }

  const sortable = Sortable.create(el, {
    animation: 150,
    handle: ".drag-handle-type",
    ghostClass: "drag-ghost",
    onEnd() {
      updateTypeSortorder(groupId);
      flashSaved(`#group-${groupId} .types-container`);
    }
  });

  typeSortables.set(groupId, sortable);
}

function initTypeSortAll() {
  groups.value.forEach(g => initTypeSort(g.id));
}

function updateGroupSortorder() {
  groups.value = [...document.querySelectorAll(".type-group")].map(
    (el, idx) => {
      const id = Number(el.dataset.id);
      const group = groups.value.find(g => g.id === id);
      return { ...group, sortorder: idx + 1 };
    }
  );

  saveSortorderToServer();
}

function updateTypeSortorder(groupId) {
  const container = document.querySelector(`#group-${groupId} .types-container`);
  if (!container) return;

  const group = groups.value.find(g => g.id === groupId);

  group.types = [...container.querySelectorAll(".type-row")].map(
    (el, idx) => {
      const id = Number(el.dataset.id);
      const type = group.types.find(t => t.id === id);
      return { ...type, sortorder: idx + 1 };
    }
  );

  saveSortorderToServer();
}

async function saveSortorderToServer() {
  const apiKey = localStorage.getItem("api_key");

  const payload = {
    groups: groups.value.map(g => ({
      id: g.id,
      sortorder: g.sortorder
    })),
    types: groups.value.flatMap(g =>
      g.types.map(t => ({
        id: t.id,
        groupId: g.id,
        sortorder: t.sortorder
      }))
    )
  };

  await fetch(`/api/articles/${props.article.id}/types/sortorder`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(apiKey ? { "x-api-key": apiKey } : {})
    },
    body: JSON.stringify(payload)
  });
}

/* =============================================
   MOUNT SEQUENCE
============================================= */
onMounted(async () => {
  Sortable = (await import("sortablejs")).default;

  await load();
  await nextTick();
  await nextTick();

  initGroupSort();
  initTypeSortAll();
});
</script>


<template>
  <div class="types-editor" ref="editorRoot">

    <div v-if="loading" class="nz-loading">Loading types…</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>

    <div v-else class="group-list">

      <div
        v-for="g in groups"
        :key="g.id"
        class="type-group"
        :id="'group-' + g.id"
        :data-id="g.id"
        :class="{ collapsed: collapsed[g.id] }"
      >

        <div class="type-group-header">
          <span class="drag-handle-group" @click.stop>☰</span>
          <span class="group-label" @click="collapsed[g.id] = !collapsed[g.id]">
            {{ g.label }}
            <template v-if="g.labelnz">
              &nbsp;- {{ g.labelnz }}
            </template>
          </span>
          <span
          :class="{
            'nz-acc-plus icon-plus': collapsed[g.id],
            'nz-acc-minus icon-minus': !collapsed[g.id]
          }"
          @click="collapsed[g.id] = !collapsed[g.id]">&nbsp;</span>
        </div>

        <div class="type-group-content" v-if="!collapsed[g.id]">
          <div class="types-container">

            <div
              v-for="t in g.types"
              :key="t.id"
              class="type-row"
              :data-id="t.id"
              :data-nz-valid="selected.includes(t.id)"
            >
              <span class="icon-move drag-handle-type" @click.stop>&nbsp;</span>

              <label>
                <input
                  type="checkbox"
                  :checked="selected.includes(t.id)"
                  @change="toggleType(t.id)"
                />
                {{ t.label }}
              </label>
            </div>

          </div>
        </div>

      </div>
    </div>

    <div class="nz-modal-footer">
      <button class="btn btn-primary" @click="save">Save</button>
      <button class="btn btn-warning" @click="cancel">Cancel</button>
    </div>

  </div>
</template>

<style scoped>
.error-msg {
  padding: 20px;
  color: red;
}

/* Group container */
.type-group {
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 12px;

}

/* Header */
.type-group-header {
  padding: 10px 14px;
  background: linear-gradient(#fff, #f0f0f0);
  border-bottom: 1px solid #ddd;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  user-select: none;
  transition: all 0.3s ease;
}

/* Collapsed */
.type-group.collapsed .type-group-content {
  display: none;
}

/* Content */
.type-group-content {
  padding: 10px 10px 10px 13px;

}

.type-group,
.type-row {
  transition: transform 150ms ease, opacity 150ms ease;
}

.type-row {
  margin-bottom: 8px;
  font-size: 14px;
}

.type-row:last-child {
  margin-bottom: 0;
}

.type-row input {
  margin-right: 8px;
}

.arrow {
  font-size: 14px;
  opacity: 0.7;
}

/* Dark mode compatibility (optional) */
body.dark .type-group {
  background: #2b2b2b;
  border-color: #555;
}

body.dark .type-group-header {
  background: linear-gradient(#333, #222);
  border-bottom-color: #555;
  color: #eee;
}

body.dark .type-group-content {
  background: #242424;
}

body.dark .type-row label {
  color: #ddd;
}
</style>
